import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { useCompany } from '../../../contexts/CompanyContext';
import { useAuth } from '../../../contexts/AuthContext';


const MultiCompanySwitcher = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    activeCompanyId, 
    activeCompany, 
    companies, 
    switchCompany, 
    loading,
    hasMultipleCompanies 
  } = useCompany();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter companies based on search
  const filteredCompanies = companies?.filter(company =>
    company?.company_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    company?.company_tag?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const handleCompanySwitch = (companyId) => {
    switchCompany(companyId);
    setIsDropdownOpen(false);
    setSearchTerm('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading companies...</p>
        </div>
      </div>
    );
  }

  if (!hasMultipleCompanies) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-border p-8 text-center">
          <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Building2" size={32} color="#64748B" />
          </div>
          <h2 className="text-xl font-semibold text-text-primary mb-2">
            Single Company Access
          </h2>
          <p className="text-text-secondary mb-6">
            You currently have access to only one company. The company switcher is available when you belong to multiple organizations.
          </p>
          {activeCompany && (
            <div className="bg-secondary-50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center space-x-3">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">
                    {activeCompany?.company_tag?.slice(0, 2)?.toUpperCase()}
                  </span>
                </div>
                <div className="text-left">
                  <p className="font-medium text-text-primary">{activeCompany?.company_name}</p>
                  <p className="text-sm text-text-secondary">@{activeCompany?.company_tag}</p>
                </div>
              </div>
            </div>
          )}
          <button
            onClick={() => navigate('/corporate/dashboard')}
            className="w-full px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-secondary-50 rounded-lg transition-colors duration-200"
              >
                <Icon name="ArrowLeft" size={20} color="#64748B" />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-text-primary">Company Switcher</h1>
                <p className="text-sm text-text-secondary mt-1">
                  Switch between {companies?.length} organizations
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>
      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Active Company Card */}
        <div className="bg-white rounded-xl shadow-sm border border-border p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
              <Icon name="Building2" size={20} color="#3B82F6" />
              <span>Current Company</span>
            </h2>
            <span className="px-3 py-1 bg-success-50 text-success-700 text-xs font-medium rounded-full">
              Active
            </span>
          </div>
          
          {activeCompany ? (
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">
                  {activeCompany?.company_tag?.slice(0, 2)?.toUpperCase()}
                </span>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-text-primary mb-1">
                  {activeCompany?.company_name}
                </h3>
                <div className="flex items-center space-x-4 text-sm text-text-secondary">
                  <span className="flex items-center space-x-1">
                    <Icon name="Tag" size={14} />
                    <span>@{activeCompany?.company_tag}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <Icon name="Shield" size={14} />
                    <span className="capitalize">{activeCompany?.role?.replace('_', ' ')}</span>
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-text-secondary">No active company selected</p>
          )}
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Icon 
              name="Search" 
              size={20} 
              color="#64748B" 
              className="absolute left-4 top-1/2 transform -translate-y-1/2"
            />
            <input
              type="text"
              placeholder="Search companies by name or tag..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-text-primary placeholder-text-secondary"
            />
          </div>
        </div>

        {/* Companies List */}
        <div className="bg-white rounded-xl shadow-sm border border-border overflow-hidden">
          <div className="px-6 py-4 border-b border-border bg-secondary-50">
            <h2 className="text-lg font-semibold text-text-primary">
              All Companies ({filteredCompanies?.length})
            </h2>
          </div>

          <div className="divide-y divide-border">
            {filteredCompanies?.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <Icon name="Search" size={48} color="#CBD5E1" className="mx-auto mb-4" />
                <p className="text-text-secondary">No companies found matching your search</p>
              </div>
            ) : (
              filteredCompanies?.map((company) => (
                <div
                  key={company?.id}
                  className={`px-6 py-4 hover:bg-secondary-50 transition-colors duration-200 cursor-pointer ${
                    company?.id === activeCompanyId ? 'bg-primary-50' : ''
                  }`}
                  onClick={() => handleCompanySwitch(company?.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        company?.id === activeCompanyId ? 'bg-primary' : 'bg-secondary-200'
                      }`}>
                        <span className={`font-bold ${
                          company?.id === activeCompanyId ? 'text-white' : 'text-text-secondary'
                        }`}>
                          {company?.company_tag?.slice(0, 2)?.toUpperCase()}
                        </span>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-semibold text-text-primary truncate">
                            {company?.company_name}
                          </h3>
                          {company?.id === activeCompanyId && (
                            <span className="px-2 py-0.5 bg-primary text-white text-xs font-medium rounded-full flex-shrink-0">
                              Active
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-3 text-sm text-text-secondary">
                          <span className="flex items-center space-x-1">
                            <Icon name="Tag" size={12} />
                            <span>@{company?.company_tag}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Icon name="Shield" size={12} />
                            <span className="capitalize">{company?.role?.replace('_', ' ')}</span>
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            company?.status === 'active' ?'bg-success-50 text-success-700' :'bg-secondary-100 text-secondary-600'
                          }`}>
                            {company?.status}
                          </span>
                        </div>
                      </div>
                    </div>

                    {company?.id === activeCompanyId ? (
                      <div className="flex items-center space-x-2 text-primary">
                        <Icon name="Check" size={20} />
                        <span className="text-sm font-medium">Current</span>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleCompanySwitch(company?.id);
                        }}
                        className="px-4 py-2 text-sm font-medium text-primary hover:bg-primary-50 rounded-lg transition-colors duration-200 border border-primary"
                      >
                        Switch
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Helper Text */}
        <div className="mt-6 bg-secondary-50 rounded-lg p-4 flex items-start space-x-3">
          <Icon name="Info" size={20} color="#3B82F6" className="flex-shrink-0 mt-0.5" />
          <div className="text-sm text-text-secondary">
            <p className="font-medium text-text-primary mb-1">About Company Switching</p>
            <p>
              When you switch companies, all pages will automatically update to show data for the selected organization. 
              Your selection persists across sessions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiCompanySwitcher;